import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-menu-item.css?inline'

// Presentational menu-item tile shared by `bb-menu` (grid of items)
// and `bb-chat` (suggestion strip). The host owns *no* domain logic —
// it just renders the fields it's given and dispatches `item-pick` when
// clicked, leaving the panel to decide what to do (open the item, send an
// utterance, etc.). Image-load failures fall back to an empty tile (avoids
// the browser's broken-image glyph during BerryCore re-seeds).
@customElement('bb-menu-item')
export class BbMenuItem extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property() name = ''
  @property() description = ''
  @property() price = ''
  // Optional category label rendered above the name; bb-menu uses it to
  // keep category context visible after sections were flattened into a
  // single grid. Empty string = nothing rendered.
  @property() category = ''
  @property({ attribute: 'image-url' }) imageUrl: string | null = null
  // Optional modifier-group summary — rendered as a subtle italic line
  // below the description so customers can see at a glance which items
  // are configurable (required choice / extras / removable ingredients)
  // before clicking. The picker still opens on click; this is just an
  // informational nudge.
  @property({ attribute: false })
  modifierGroups: Array<{
    name: string
    required: boolean
    groupType: 'choices' | 'adjustments'
  }> = []
  // 'md' (default) — full-width row tile used in bb-menu.
  // 'sm' — compact card used in bb-chat's suggestion strip.
  @property({ reflect: true }) size: 'sm' | 'md' = 'md'
  @property({ type: Boolean, reflect: true }) disabled = false

  @state() private failed = false

  constructor() {
    super()
    // Host-level click handler so clicking anywhere on the tile (and
    // synthetic `.click()` calls in tests targeting the host element)
    // fires the pick — not just the inner .item div.
    this.addEventListener('click', this.onClick)
  }

  render() {
    return html`
      <div class="item">
        <div class="tile">
          ${this.imageUrl && !this.failed
            ? html`<img
                src=${this.imageUrl}
                alt=${this.name}
                loading="lazy"
                decoding="async"
                @error=${() => (this.failed = true)}
              />`
            : html`<svg
                class="fallback"
                viewBox="2 2 20 20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M7 21h10" />
                <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z" />
                <path d="M11.38 12a2.4 2.4 0 0 1-.4-4.77 2.4 2.4 0 0 1 3.2-2.77 2.4 2.4 0 0 1 3.47-.63 2.4 2.4 0 0 1 3.37 3.37 2.4 2.4 0 0 1-1.1 3.7 2.51 2.51 0 0 1 .03 1.1" />
                <path d="m13 12 4-4" />
                <path d="M10.9 7.25A3.99 3.99 0 0 0 4 10c0 .73.2 1.41.54 2" />
              </svg>`}
        </div>
        <div class="info">
          ${this.category
            ? html`<div class="category">${this.category}</div>`
            : nothing}
          <div class="name">${this.name}</div>
          ${this.description
            ? html`<div class="desc">${this.description}</div>`
            : nothing}
          ${this.price
            ? html`<div class="price">${this.price}</div>`
            : nothing}
          ${this.size === 'md' && this.modifierGroups.length > 0
            ? html`<div class="mods" title="Click the card to customize">
                ${this.modifierGroups.some((g) => g.required)
                  ? html`<span class="mod mod-req">Required picks</span>`
                  : nothing}
                <span class="mod mod-opt">Customizable</span>
              </div>`
            : nothing}
        </div>
      </div>
    `
  }

  private onClick = () => {
    if (this.disabled) return
    this.dispatchEvent(
      new CustomEvent('item-pick', { bubbles: true, composed: true }),
    )
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-menu-item': BbMenuItem
  }
}
